import React from 'react';
import {
  CheckmarkCircle16Filled,
  Circle16Regular,
  DismissCircle16Filled,
  LockClosed16Filled,
  QuestionCircle16Filled,
  SubtractCircle16Regular,
  Warning16Filled,
} from '@fluentui/react-icons';
import { ReactComponent as ActionStatusRunningIcon } from '../../assets/img/icons/action_status_running.svg';

import { getActionStatusColor, normalizeActionStatus } from '../../utils/actionStatusConfig';
import { ActionStatus } from '../../types/actionTypes';

/**
 * Fluent status badges shared by every surface that shows an action or target
 * status. Running uses the Stitch Azure pipeline SVG asset; other statuses use
 * Fluent glyphs with semantic colors from getActionStatusColor.
 */

const CANCELLING_STATUSES = new Set(['CANCELLING']);
const MIXED_STATUSES = new Set(['MIXED']);
const QUEUED_STATUSES = new Set(['INITIALIZED', 'QUEUED', 'PENDING']);
const RUNNING_STATUSES = new Set(['IN_PROGRESS', 'RUNNING', 'STARTED']);

/** Default glyph size for list/detail action status icons. */
export const ACTION_STATUS_ICON_SIZE = 20;

interface GlyphProps {
  className?: string;
  size?: number;
  color: string;
}

const FluentStatusIcon: React.FC<GlyphProps & { spinning?: boolean; children: React.ReactNode }> = ({
  className,
  size = ACTION_STATUS_ICON_SIZE,
  color,
  spinning,
  children,
}) => (
  <span
    className={`action-status-badge${spinning ? ' action-status-badge--spin' : ''}${className ? ` ${className}` : ''}`}
    style={{ color, width: size, height: size, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
  >
    {children}
  </span>
);

/**
 * Running/cancelling badge: Stitch Azure three-arc SVG.
 * - `spin={false}`: static arcs (e.g. Visible-targets “in progress” count at 0)
 * - `spin={true}`: rotates while an action/target is actually in progress
 */
export const RunningStatusBadge: React.FC<Omit<GlyphProps, 'color'> & { color?: string; spin?: boolean }> = ({
  className,
  size = ACTION_STATUS_ICON_SIZE,
  spin = true,
}) => (
  <span
    className={`action-status-badge action-status-badge--running${spin ? ' action-status-badge--spin' : ''}${className ? ` ${className}` : ''}`}
    style={{ width: size, height: size }}
  >
    <ActionStatusRunningIcon aria-hidden="true" width={size} height={size} focusable="false" />
  </span>
);

/** Renders the badge for `status` at `size` px. */
export const ActionStatusGlyph: React.FC<{ status: ActionStatus | null | undefined; className?: string; size?: number }> = ({
  status,
  className,
  size = ACTION_STATUS_ICON_SIZE,
}) => {
  const normalized = normalizeActionStatus(status).toUpperCase();
  const color = getActionStatusColor(normalized);
  const iconStyle = { width: size, height: size, flexShrink: 0 };

  if (RUNNING_STATUSES.has(normalized) || CANCELLING_STATUSES.has(normalized)) {
    // Actual in-progress / cancelling rows always spin; count chips set spin explicitly.
    return <RunningStatusBadge className={className} size={size} spin />;
  }

  if (QUEUED_STATUSES.has(normalized)) {
    return (
      <FluentStatusIcon className={className} size={size} color={color}>
        <Circle16Regular aria-hidden="true" style={iconStyle} />
      </FluentStatusIcon>
    );
  }

  let glyph: React.ReactNode;
  switch (normalized) {
    case 'COMPLETED':
    case 'SUCCEEDED':
    case 'SUCCESS':
      glyph = <CheckmarkCircle16Filled aria-hidden="true" style={iconStyle} />;
      break;
    case 'FAILED':
    case 'FAILURE':
    case 'TIMED_OUT':
    case 'CANCELLED':
    case 'CANCELED':
      glyph = <DismissCircle16Filled aria-hidden="true" style={iconStyle} />;
      break;
    case 'BLOCKED':
    case 'BUSY':
      glyph = <LockClosed16Filled aria-hidden="true" style={iconStyle} />;
      break;
    case 'PARTIAL':
    case 'PARTIAL_SUCCESS':
      glyph = <Warning16Filled aria-hidden="true" style={iconStyle} />;
      break;
    case 'OUTCOME_UNCONFIRMED':
      glyph = <QuestionCircle16Filled aria-hidden="true" style={iconStyle} />;
      break;
    case 'SKIPPED':
      glyph = <SubtractCircle16Regular aria-hidden="true" style={iconStyle} />;
      break;
    default:
      glyph = MIXED_STATUSES.has(normalized)
        ? <Warning16Filled aria-hidden="true" style={iconStyle} />
        : <QuestionCircle16Filled aria-hidden="true" style={iconStyle} />;
  }

  return (
    <FluentStatusIcon className={className} size={size} color={color}>
      {glyph}
    </FluentStatusIcon>
  );
};

export default ActionStatusGlyph;
